import { deprecate } from '@ember/debug';
import { action } from '@ember/object';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_SHORT } from '@mylighthouse/prism-foundation';
import type { FlashMessagesService, FlashObjectOptions } from 'ember-cli-flash';

import type { ToastAction } from '../types/components/prism/toast-queue.ts';

type ToastOptions = Partial<FlashObjectOptions>;

type FlashOptions = ToastOptions & {
  destroyOnClick: boolean;
  extendedTimeout: number;
  status: ToastStatus;
  timeout: number;
};

export const TOAST_STATUS_MAP = {
  automationOn: 'automation-on',
  automationOff: 'automation-off',
  error: 'error',
  info: 'info',
  success: 'success',
  warning: 'warning',
} as const;

export type ToastStatus =
  (typeof TOAST_STATUS_MAP)[keyof typeof TOAST_STATUS_MAP];

export interface ToastUpdateOptions {
  action?: ToastAction;
  message?: string;
}

/**
 * Custom properties on FlashObject are copied as plain (non-@tracked)
 * properties in the constructor. Action provides a @tracked wrapper
 * so it's possible to reactively update the toast component.
 *
 * @see https://github.com/adopted-ember-addons/ember-cli-flash/blob/master/src/flash/object.ts
 */
export class TrackedValue<T> {
  @tracked value;

  constructor(value: T) {
    this.value = value;
  }
}

let toastIdCounter = 0;

function generateToastId(): string {
  return `toast-${++toastIdCounter}`;
}

export default class ToastService extends Service {
  @service declare flashMessages: FlashMessagesService;

  get queue(): typeof this.flashMessages.queue {
    return this.flashMessages.queue;
  }

  private addToast(options: ToastOptions): string {
    const flashOptions = this.getFlashOptions(options);

    const toastId = generateToastId();

    this.flashMessages.add({
      ...flashOptions,
      id: toastId,
      action: new TrackedValue(flashOptions.action),
    });

    return toastId;
  }

  private getFlashOptions(options: ToastOptions = {}): FlashOptions {
    if (options['title']) {
      deprecate(
        'The title property is deprecated. And will be removed in a future version. Please use the message property instead.',
        false,
        {
          id: 'prism-ember.toast.title-deprecated',
          until: 'next',
          for: 'prism-ember',
          since: { available: '38' },
        },
      );
    }

    const defaultOptions = {
      destroyOnClick: false,
      extendedTimeout: 500,
      status: TOAST_STATUS_MAP.info,
      timeout: TOAST_TIMEOUT_SHORT,
    };

    return {
      ...defaultOptions,
      ...options,
    };
  }

  @action alert(message: string, options?: ToastOptions): string {
    return this.addToast({
      message,
      type: 'warning',
      status: 'warning',
      ...options,
    });
  }

  @action automationOff(message: string, options?: ToastOptions): string {
    return this.addToast({
      message,
      status: 'automation-off',
      type: 'info',
      ...options,
    });
  }

  @action automationOn(message: string, options?: ToastOptions): string {
    return this.addToast({
      message,
      type: 'info',
      status: 'automation-on',
      ...options,
    });
  }

  @action close(toastId: string): void {
    this.flashMessages.removeBy('id', toastId);
  }

  @action error(message: string, options?: ToastOptions): string {
    return this.addToast({
      message,
      type: 'danger',
      status: 'error',
      ...options,
    });
  }

  @action info(message: string, options?: ToastOptions): string {
    return this.addToast({ message, type: 'info', status: 'info', ...options });
  }

  @action success(message: string, options?: ToastOptions): string {
    return this.addToast({
      message,
      type: 'success',
      status: 'success',
      ...options,
    });
  }

  @action update(toastId: string, partial: Partial<ToastUpdateOptions>): void {
    const flashMessage = this.flashMessages.findBy('id', toastId);

    if (!flashMessage) {
      return;
    }

    if ('message' in partial && partial.message !== undefined) {
      flashMessage.message = partial.message;
    }

    if ('action' in partial && flashMessage.action instanceof TrackedValue) {
      flashMessage.action.value = partial.action;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    toast: ToastService;
  }
}
